import type { ReactNode } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import type { UiTheme } from "../theme";

// <AnchoredMenu> — a dropdown/popover that hangs off a trigger and dismisses on
// an outside tap. Extracted from SettingsSelectChoice's anchored-dropdown
// technique (position:relative anchor + a resting zIndex that lifts on open so
// the menu paints OVER the following siblings on web — a plain zIndex on the
// inner menu isn't enough — plus a full-bleed transparent backdrop for
// outside-tap dismiss). That was the only working corner/inline-anchored menu
// in the repo; this is its 2nd caller (the Me avatar-menu), so the idiom moves
// here once instead of being hand-rolled a third time.
//
// The trigger's press state is the caller's business (it owns `open`); this
// primitive only positions the menu and renders the dismiss backdrop. Placement
// lets one trigger open upward (a rail-bottom avatar) or downward (a header
// avatar) without the menu leaving the viewport.
//
// a11y: the backdrop is a labelled Pressable so a screen reader can dismiss;
// the trigger carries its own expanded state from the caller.

export type AnchoredMenuPlacement = "top" | "bottom";
export type AnchoredMenuAlign = "left" | "right";

export type AnchoredMenuProps = {
  theme: UiTheme;
  open: boolean;
  onClose: () => void;
  /** The trigger element (e.g. a Pressable wrapping an Avatar). The caller
   *  wires its onPress to toggle `open`. */
  trigger: ReactNode;
  children: ReactNode;
  /** Screen-reader label for the outside-tap dismiss backdrop. The caller
   *  supplies it translated — this primitive owns no copy (reusable-UI rule). */
  dismissLabel: string;
  /** Which side of the trigger the menu opens on. Default "bottom". */
  placement?: AnchoredMenuPlacement;
  /** Which edge the menu aligns to. Default "left". */
  align?: AnchoredMenuAlign;
  minWidth?: number;
};

export function AnchoredMenu({
  theme,
  open,
  onClose,
  trigger,
  children,
  dismissLabel,
  placement = "bottom",
  align = "left",
  minWidth = 240,
}: AnchoredMenuProps) {
  const styles = makeStyles(theme);
  return (
    <View style={[styles.anchor, open && styles.anchorOpen]}>
      {trigger}
      {open ? (
        <>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={dismissLabel}
            onPress={onClose}
            style={styles.backdrop}
          />
          <View
            style={[
              styles.menu,
              { minWidth },
              placement === "top" ? styles.placeTop : styles.placeBottom,
              align === "right" ? styles.alignRight : styles.alignLeft,
            ]}
          >
            {children}
          </View>
        </>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r } = theme;
  return StyleSheet.create({
    // relative + a resting zIndex so the open elevation has a stacking context.
    anchor: { position: "relative", zIndex: 1 },
    // Open: sits above sibling rows so the absolute menu isn't covered (web
    // sibling paint-order — mirrors SettingsSelectChoice.cardOpen).
    anchorOpen: { zIndex: 100, elevation: 24 },
    backdrop: {
      position: "absolute",
      top: -2000,
      left: -2000,
      right: -2000,
      bottom: -2000,
      zIndex: 10,
    },
    menu: {
      position: "absolute",
      backgroundColor: p.surface,
      borderRadius: r.r4,
      borderWidth: 1,
      borderColor: p.borderStrong,
      paddingVertical: s.s1,
      zIndex: 11,
      elevation: 8,
    },
    placeBottom: { top: "100%", marginTop: s.s2 },
    placeTop: { bottom: "100%", marginBottom: s.s2 },
    alignLeft: { left: 0 },
    alignRight: { right: 0 },
  });
}
